import React, { useEffect, useState } from "react";
import styled from "styled-components";
import { useAuthStore } from "../../store/useStore";
import PageHeader from "../../components/common/PageHeader";
import { listReviews, loadPhoto, deletePhoto } from "./api";
import ReviewItem from "./ReviewItem";

const Workspace = styled.div`
  padding: 28px 40px; color: #334155;
  .review-grid { display: grid; grid-template-columns: minmax(220px, 320px) minmax(0, 1fr); gap: 24px; align-items: start; }
  nav, article { min-width: 0; }
  button { padding: 10px 14px; border: 1px solid #d9dee5; border-radius: 8px; background: white; color: #334155; cursor: pointer; margin: 4px 4px 4px 0; }
  button:disabled { opacity: .5; cursor: default; }
  .primary, button[aria-current="true"] { background: #d2691e; color: white; border-color: #d2691e; }
  nav > button { display: block; width: 100%; text-align: left; padding: 16px; margin-bottom: 8px; }
  .review-item { background: white; border: 1px solid #e2e8f0; border-radius: 12px; padding: 20px; margin: 20px 0; }
  fieldset { border: 0; padding: 0; min-width: 0; }
  label { display: flex; flex-direction: column; gap: 8px; margin: 12px 0; font-size: 14px; }
  input, select, textarea { border: 1px solid #cbd5e1; border-radius: 6px; padding: 10px; min-width: 0; font: inherit; }
  .nutrient-grid { display: grid; grid-template-columns: repeat(3, minmax(0, 1fr)); gap: 0 12px; }
  summary { cursor: pointer; padding: 16px 0; }
  p { line-height: 1.7; font-size: 14px; }
  [role="alert"] { color: #b91c1c; }
  img { width: 100%; max-width: 440px; max-height: 360px; object-fit: contain; background: #f8fafc; border-radius: 12px; }
  @media (max-width: 850px) { padding: 20px; .review-grid { grid-template-columns: 1fr; } .nutrient-grid { grid-template-columns: repeat(2, minmax(0, 1fr)); } }
`;
function ReviewPhoto({ record, token, onChange }) {
  const [url, setUrl] = useState(null);
  const [error, setError] = useState("");
  const [deleting, setDeleting] = useState(false);
  useEffect(() => {
    const controller = new AbortController(); let objectUrl;
    setUrl(null); setError("");
    if (record.hasPhoto) loadPhoto(token, record.id, controller.signal).then(value => {
      objectUrl = value;
      if (controller.signal.aborted) URL.revokeObjectURL(value); else setUrl(value);
    }).catch(e => { if (!controller.signal.aborted) setError(e.message); });
    return () => { controller.abort(); if (objectUrl) URL.revokeObjectURL(objectUrl); };
  }, [record.id, record.hasPhoto, token]);
  async function remove() {
    if (!window.confirm("이 사진을 삭제할까요? 같은 사진의 모든 음식 후보에서 삭제됩니다. 영양정보와 검토 이력은 유지됩니다.")) return;
    setDeleting(true); setError("");
    try { await deletePhoto(token, record.id); await onChange(); } catch (e) { setError(e.message); } finally { setDeleting(false); }
  }
  return <div>{url && <img src={url} alt="검토할 음식 사진" />}{!record.hasPhoto && <p>삭제된 사진입니다.</p>}
    {record.hasPhoto && <button disabled={deleting} onClick={remove}>사진 삭제</button>}{error && <p role="alert">{error}</p>}</div>;
}
export default function FoodReviews() {
  const token = useAuthStore(state => state.token);
  const [records, setRecords] = useState([]);
  const [nextCursor, setNextCursor] = useState(null);
  const [selectedId, setSelectedId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [pendingOnly, setPendingOnly] = useState(false);
  async function load(append = false) {
    setLoading(true); setError("");
    try {
      const result = await listReviews(token, append ? nextCursor : null);
      setRecords(previous => append ? [...previous, ...result.data.items] : result.data.items);
      setNextCursor(result.data.nextCursor);
      setSelectedId(current => current || result.data.items[0]?.id || null);
    } catch (e) { setError(e.message); } finally { setLoading(false); }
  }
  useEffect(() => { load(); /* token changes reload the queue */ }, [token]); // eslint-disable-line react-hooks/exhaustive-deps
  const shown = pendingOnly ? records.filter(record => record.items.some(item => item.status === "pending")) : records;
  const selected = shown.find(record => record.id === selectedId) || shown[0];
  return <><PageHeader title="음식 사진 검토" /><Workspace>
    <p>미등록 음식 사진을 검토해 음식 DB를 보완합니다. 개인 식단의 AI 추정 기록은 승인 결과로 소급 변경되지 않습니다. 사진·개인별 분석 내역은 최대 90일 또는 탈퇴·동의 철회 시 먼저 도래하는 때 삭제합니다. 검토용 보관에 동의한 사진만 표시합니다.</p>
    <button disabled={loading} onClick={() => load()}>새로고침</button>
    <label><span><input type="checkbox" checked={pendingOnly} onChange={e => setPendingOnly(e.target.checked)} /> 불러온 목록에서 검토 대기만 보기</span></label>
    {error && <p role="alert">{error}</p>}{loading && <p role="status">불러오는 중…</p>}
    {!loading && !records.length && !error && <p>검토할 미등록 음식 사진이 없습니다.</p>}
    <div className="review-grid"><nav aria-label="사진 검토 목록">{shown.map(record => <button key={record.id} aria-current={record.id === selectedId ? "true" : "false"} onClick={() => setSelectedId(record.id)}>
      {record.items.map(item => item.name).join(", ")}<br /><small>{new Date(record.createdAt).toLocaleString("ko-KR")} · 대기 {record.items.filter(item => item.status === "pending").length}건</small>
    </button>)}{nextCursor && <button disabled={loading} onClick={() => load(true)}>더 불러오기</button>}</nav>
      <article>{selected && <><h2>음식 후보 검토</h2><ReviewPhoto record={selected} token={token} onChange={() => load()} />
        {selected.items.map(item => <ReviewItem key={`${selected.id}-${item.index}`} reviewId={selected.id} item={item} catalogSync={selected.catalogSync} token={token} onChange={() => load()} />)}</>}
      </article></div>
  </Workspace></>;
}
